/**
 * @fileoverview Precedent Attachment Helpers
 *
 * Purpose:
 * - Normalise AI-detected attachment references into stable attachment slots.
 * - Build download packages for generated documents and their selected files.
 *
 * Attachments intentionally stay separate from precedent merge fields: they
 * represent binary files that accompany a letter/form rather than values that
 * get substituted into `{{field}}` placeholders.
 */

import JSZip from "jszip";
import { PDFDocument } from "pdf-lib";
import { arrayBufferBytes } from "@cvx/lib/arrayBufferBytes";

export type PrecedentAttachmentKey =
  "notice_of_appointment" | "court_order" | "rocap" | "custom";

export type PrecedentAttachmentSourceKind =
  | "template_default"
  | "job_document"
  | "generated_precedent"
  | "upload"
  | "none";

export interface ExtractedAttachmentInput {
  name: string;
  description?: string;
  required: boolean;
}

export interface AttachmentRequirement extends ExtractedAttachmentInput {
  attachmentKey: PrecedentAttachmentKey;
  /** Stable slug for `custom` attachments; shared key is `custom:<customRef>`. */
  customRef?: string;
  sourceKind: PrecedentAttachmentSourceKind;
  documentCategory?: string;
  acceptedFileTypes?: string[];
  storageId?: string;
  fileName?: string;
  fileType?: string;
  fileSize?: number;
  generatedTemplateKey?: string;
}

export interface AttachmentPackageInput {
  generatedFileName: string;
  generatedFile: Blob;
  attachments: Array<{
    fileName: string;
    file: Blob;
  }>;
}

const PDF_FILE_TYPES = ["application/pdf", ".pdf"];

/**
 * Build a UI-safe slot key for attachment state.
 *
 * Multiple configured attachments can intentionally share the same canonical
 * type (for example two court-order attachments with different sources), so
 * the attachmentKey alone is not unique enough for runtime selection state.
 */
export function buildAttachmentSlotKey(
  attachmentKey: PrecedentAttachmentKey,
  index: number,
): string {
  return `${attachmentKey}:${index}`;
}

function normaliseName(value: string): string {
  return value.trim().replace(/\s+/g, " ").toLowerCase();
}

/**
 * Convert an attachment name into a stable slug used as its reference id.
 *
 * "Form 505" → "form_505". This is how a `custom` attachment is matched across
 * templates and resolved once per job, so it must be deterministic and
 * case-insensitive.
 */
export function slugifyAttachmentName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/**
 * Compute the shared reference key for an attachment.
 *
 * Fixed types (NOA / court order / ROCAP) share by their `attachmentKey`.
 * `custom` attachments share by `custom:<slug>`, where the slug comes from the
 * stored `customRef` (falling back to a slug of the name). Two templates that
 * declare a custom attachment with the same name therefore resolve to the same
 * reference key and are filled together at job time.
 */
export function buildAttachmentReferenceKey(input: {
  attachmentKey: PrecedentAttachmentKey;
  name: string;
  customRef?: string;
}): string {
  if (input.attachmentKey !== "custom") return input.attachmentKey;
  const slug =
    (input.customRef && input.customRef.trim()) ||
    slugifyAttachmentName(input.name);
  return `custom:${slug}`;
}

/**
 * Normalise a free-text attachment reference from extraction into a typed slot.
 *
 * @param input The raw attachment object returned by precedent extraction.
 * @returns A stable requirement used by settings, generation, and export.
 */
export function normaliseAttachmentRequirement(
  input: ExtractedAttachmentInput,
): AttachmentRequirement {
  const name = normaliseName(input.name);

  if (
    name.includes("notice of appointment") ||
    name === "noa" ||
    name.includes("form 505")
  ) {
    return {
      ...input,
      name: "Notice of Appointment",
      attachmentKey: "notice_of_appointment",
      sourceKind: "job_document",
      documentCategory: "notice_of_appointment",
      acceptedFileTypes: PDF_FILE_TYPES,
    };
  }

  if (name.includes("court order") || name.includes("court ordered")) {
    return {
      ...input,
      name: "Court Order",
      attachmentKey: "court_order",
      sourceKind: "job_document",
      documentCategory: "legal_disputes",
      acceptedFileTypes: PDF_FILE_TYPES,
    };
  }

  if (name.includes("rocap") || name.includes("report on company activities")) {
    return {
      ...input,
      name: "ROCAP",
      attachmentKey: "rocap",
      sourceKind: "job_document",
      documentCategory: "director_disclosures",
      acceptedFileTypes: PDF_FILE_TYPES,
    };
  }

  return {
    ...input,
    attachmentKey: "custom",
    customRef: slugifyAttachmentName(input.name),
    // Custom named attachments resolve from an uploaded job document; the
    // chosen file is shared across every precedent using the same reference.
    sourceKind: "job_document",
  };
}

/**
 * An attachment row shaped for the `updateAttachments` mutation, whose
 * `attachmentKey` / `sourceKind` validators accept `undefined` (the server
 * infers them from the name) but not the editor's `""` placeholders.
 */
export type SaveableAttachmentRequirement = Omit<
  AttachmentRequirement,
  "attachmentKey" | "sourceKind"
> & {
  attachmentKey?: PrecedentAttachmentKey;
  sourceKind?: PrecedentAttachmentSourceKind;
};

/**
 * Filter and clean locally edited attachment rows before persisting.
 *
 * The attachments editor adds new rows with empty-string placeholder values
 * for `attachmentKey` / `sourceKind` so its selects render unselected, but
 * the backend's literal-union validators reject `""` (MOR-553). Incomplete
 * rows stay local until the user has configured enough for the row to be
 * meaningful:
 * - a source must be chosen;
 * - `job_document` rows also need a type, and `custom` ones a name that
 *   yields a non-empty shared reference slug.
 *
 * Remaining empty-string placeholders on rows that do save (e.g. the type of
 * a `template_default` row) are coerced to `undefined` so the server infers
 * them from the attachment name.
 */
export function prepareAttachmentRequirementsForSave(
  attachments: AttachmentRequirement[],
): SaveableAttachmentRequirement[] {
  return attachments
    .filter((attachment) => {
      if (!attachment.sourceKind) return false;
      if (attachment.sourceKind !== "job_document") return true;
      if (!attachment.attachmentKey) return false;
      if (attachment.attachmentKey === "custom") {
        return slugifyAttachmentName(attachment.name).length > 0;
      }
      return true;
    })
    .map((attachment) => ({
      ...attachment,
      attachmentKey: attachment.attachmentKey || undefined,
      sourceKind: attachment.sourceKind || undefined,
    }));
}

/**
 * Build a zip containing a generated Word document and separate attachments.
 *
 * @param input The generated document plus selected attachment blobs.
 * @returns A zip blob ready for browser download.
 */
export async function buildAttachmentDownloadPackage(
  input: AttachmentPackageInput,
): Promise<Blob> {
  const zip = new JSZip();
  zip.file(input.generatedFileName, await input.generatedFile.arrayBuffer());

  for (const attachment of input.attachments) {
    zip.file(
      `attachments/${attachment.fileName}`,
      await attachment.file.arrayBuffer(),
    );
  }

  return await zip.generateAsync({ type: "blob" });
}

/**
 * Zip an arbitrary set of files, preserving any folder paths embedded in
 * their names (e.g. "letter-a-attachments/schedule.docx"). Used by the bulk
 * day-one download to ship one consolidated PDF per document (MOR-847).
 */
export async function buildZipArchive(
  files: Array<{ fileName: string; file: Blob }>,
): Promise<Blob> {
  const zip = new JSZip();
  for (const entry of files) {
    zip.file(entry.fileName, await entry.file.arrayBuffer());
  }
  return await zip.generateAsync({ type: "blob" });
}

// ---------------------------------------------------------------------------
// PDF consolidation (MOR-301)
//
// `consolidateAttachmentsIntoPdf` merges every PDF and image attachment into
// the base PDF as additional pages, returning any remaining attachment types
// (e.g. .docx, .xlsx) so the caller can ship them in a sibling zip. PDFs use
// pdf-lib's `copyPages`; images are embedded via `embedJpg`/`embedPng` and
// drawn on a fresh A4 page scaled to fit while preserving aspect ratio.
// ---------------------------------------------------------------------------

export interface ConsolidatableAttachment {
  fileName: string;
  file: Blob;
  fileType?: string;
}

export interface ConsolidationResult {
  consolidated: Blob;
  remaining: Array<{ fileName: string; file: Blob }>;
}

/** A4 in PDF points (1pt = 1/72in). */
const A4_WIDTH_PT = 595.28;
const A4_HEIGHT_PT = 841.89;

function isPdfAttachment(attachment: ConsolidatableAttachment): boolean {
  return (
    attachment.file.type === "application/pdf" ||
    attachment.fileType === "application/pdf" ||
    attachment.fileName.toLowerCase().endsWith(".pdf")
  );
}

function isJpegAttachment(attachment: ConsolidatableAttachment): boolean {
  const lower = attachment.fileName.toLowerCase();
  return (
    attachment.file.type === "image/jpeg" ||
    attachment.fileType === "image/jpeg" ||
    lower.endsWith(".jpg") ||
    lower.endsWith(".jpeg")
  );
}

function isPngAttachment(attachment: ConsolidatableAttachment): boolean {
  return (
    attachment.file.type === "image/png" ||
    attachment.fileType === "image/png" ||
    attachment.fileName.toLowerCase().endsWith(".png")
  );
}

/**
 * Concatenate multiple PDFs into one, preserving input order. Used by the
 * bulk day-one download's "combined PDF" option to merge every selected
 * document's consolidated PDF into a single file (MOR-850).
 */
export async function mergePdfBlobs(pdfs: Blob[]): Promise<Blob> {
  const merged = await PDFDocument.create();
  for (const blob of pdfs) {
    const pdf = await PDFDocument.load(await blob.arrayBuffer());
    const pages = await merged.copyPages(pdf, pdf.getPageIndices());
    for (const page of pages) merged.addPage(page);
  }
  const bytes = await merged.save();
  return new Blob([arrayBufferBytes(bytes)], { type: "application/pdf" });
}

/**
 * Merge a base PDF with every consolidatable attachment.
 *
 * - PDF attachments append their pages directly (matches the legacy filled-form
 *   merge behaviour).
 * - PNG/JPEG image attachments are embedded as full-page images on a fresh A4
 *   page, scaled to fit while preserving aspect ratio.
 * - Anything else (Word, Excel, archives, etc.) is returned in `remaining` so
 *   the caller can decide whether to ship it in a sibling zip.
 *
 * Attachment ordering is preserved.
 */
export async function consolidateAttachmentsIntoPdf(
  basePdf: Blob,
  attachments: ConsolidatableAttachment[],
): Promise<ConsolidationResult> {
  const merged = await PDFDocument.create();
  const base = await PDFDocument.load(await basePdf.arrayBuffer());
  const basePages = await merged.copyPages(base, base.getPageIndices());
  for (const page of basePages) merged.addPage(page);

  const remaining: Array<{ fileName: string; file: Blob }> = [];

  for (const attachment of attachments) {
    if (isPdfAttachment(attachment)) {
      try {
        const pdf = await PDFDocument.load(await attachment.file.arrayBuffer());
        const pages = await merged.copyPages(pdf, pdf.getPageIndices());
        for (const page of pages) merged.addPage(page);
      } catch (err) {
        console.error(
          `[consolidateAttachmentsIntoPdf] failed to merge PDF "${attachment.fileName}", falling back to zip`,
          err,
        );
        remaining.push({
          fileName: attachment.fileName,
          file: attachment.file,
        });
      }
      continue;
    }

    if (isPngAttachment(attachment) || isJpegAttachment(attachment)) {
      try {
        const bytes = await attachment.file.arrayBuffer();
        const image = isPngAttachment(attachment)
          ? await merged.embedPng(bytes)
          : await merged.embedJpg(bytes);
        const page = merged.addPage([A4_WIDTH_PT, A4_HEIGHT_PT]);
        const { width: pageWidth, height: pageHeight } = page.getSize();
        const scaled = image.scaleToFit(pageWidth, pageHeight);
        page.drawImage(image, {
          x: (pageWidth - scaled.width) / 2,
          y: (pageHeight - scaled.height) / 2,
          width: scaled.width,
          height: scaled.height,
        });
      } catch (err) {
        console.error(
          `[consolidateAttachmentsIntoPdf] failed to embed image "${attachment.fileName}", falling back to zip`,
          err,
        );
        remaining.push({
          fileName: attachment.fileName,
          file: attachment.file,
        });
      }
      continue;
    }

    remaining.push({ fileName: attachment.fileName, file: attachment.file });
  }

  const bytes = await merged.save();
  return {
    consolidated: new Blob([arrayBufferBytes(bytes)], {
      type: "application/pdf",
    }),
    remaining,
  };
}
